import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { searchMembers } from "@/features/chat/api";
import { useChatStore } from "@/features/chat/store/chat-store";
import type { Conversation, Member, MemberRole } from "@/features/chat/types";
import { initials } from "@/features/chat/utils";

interface GroupSettingsDialogProps {
  conversation: Conversation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupSettingsDialog({
  conversation,
  open,
  onOpenChange,
}: GroupSettingsDialogProps) {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const renameConversation = useChatStore((s) => s.renameConversation);
  const addMembers = useChatStore((s) => s.addMembers);
  const removeMember = useChatStore((s) => s.removeMember);
  const setMemberRole = useChatStore((s) => s.setMemberRole);

  const [name, setName] = useState(conversation.name);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Member[]>([]);
  const [busy, setBusy] = useState(false);

  const myRole =
    conversation.members.find((m) => String(m.id) === String(meId))?.role ?? "member";
  const canManage = myRole === "owner" || myRole === "admin";

  // Keep the rename field in sync when the conversation updates underneath us.
  useEffect(() => {
    setName(conversation.name);
  }, [conversation.name]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Search the firm directory for teammates not already in the group.
  useEffect(() => {
    if (!open || !canManage) return;
    let cancelled = false;
    void searchMembers(query).then((list) => {
      if (cancelled) return;
      const memberIds = new Set(conversation.members.map((m) => m.id));
      setResults(list.filter((u) => !memberIds.has(u.id)));
    });
    return () => {
      cancelled = true;
    };
  }, [open, query, canManage, conversation.members]);

  async function run(action: () => Promise<void>, failMsg: string) {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : failMsg);
    } finally {
      setBusy(false);
    }
  }

  const nameChanged = name.trim() && name.trim() !== conversation.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Group settings</DialogTitle>
          <DialogDescription>
            {canManage ? "Rename the group and manage members." : "Group members."}
          </DialogDescription>
        </DialogHeader>

        {/* Rename */}
        {canManage && (
          <div className="space-y-1.5">
            <Label htmlFor="group-rename">Name</Label>
            <div className="flex gap-2">
              <Input
                id="group-rename"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                type="button"
                disabled={!nameChanged || busy}
                onClick={() =>
                  void run(
                    () => renameConversation(conversation.id, name.trim()),
                    "Couldn't rename the group.",
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Members */}
        <div className="space-y-1.5">
          <Label>Members ({conversation.members.length})</Label>
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {conversation.members.map((m) => {
              const isSelf = String(m.id) === String(meId);
              const controllable = canManage && !isSelf && m.role !== "owner";
              return (
                <div key={m.id} className="flex items-center gap-2 rounded-lg px-1 py-1.5">
                  <Avatar className="size-8 border">
                    <AvatarFallback className="text-primary text-xs font-bold">
                      {initials(m.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">
                      {m.name}
                      {isSelf && " (you)"}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">{m.email}</p>
                  </div>
                  {controllable ? (
                    <>
                      <Select
                        value={m.role}
                        onValueChange={(role) =>
                          void run(
                            () => setMemberRole(conversation.id, m.id, role as MemberRole),
                            "Couldn't change the role.",
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-24 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        aria-label={`Remove ${m.name}`}
                        className="text-muted-foreground hover:text-destructive rounded p-1"
                        onClick={() =>
                          void run(
                            () => removeMember(conversation.id, m.id),
                            "Couldn't remove the member.",
                          )
                        }
                      >
                        <X className="size-4" />
                      </button>
                    </>
                  ) : (
                    <Badge variant="secondary" className="capitalize">
                      {m.role}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Add members */}
        {canManage && (
          <div className="space-y-1.5">
            <Label htmlFor="group-add">Add members</Label>
            <Input
              id="group-add"
              placeholder="Search teammates…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {results.length > 0 && (
              <div className="max-h-40 space-y-0.5 overflow-y-auto">
                {results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    disabled={busy}
                    className="hover:bg-muted flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left"
                    onClick={() =>
                      void run(
                        () => addMembers(conversation.id, [u.id]),
                        "Couldn't add the member.",
                      )
                    }
                  >
                    <Avatar className="size-8 border">
                      <AvatarFallback className="text-primary text-xs font-bold">
                        {initials(u.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-medium">{u.name}</p>
                      <p className="text-muted-foreground truncate text-xs">{u.email}</p>
                    </div>
                    <Plus className="text-muted-foreground size-4" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
